
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./StepTabs.css";
import { sharedData } from "../sharedData/sharedData";
import { diligenceObservable } from "../observers/deligenceObservable";
import { riskAssesContObservable } from "../observers/riskAssesContObservable";
import config from "../config/config";
import { userSession } from "../observers/userObserver";
// Lucide Icons
import {
  Handshake,
  MapPinCheck,
  UserRoundPen,
  ShieldQuestion,
  ShieldEllipsis,
  ShieldPlus
} from "lucide-react";

// Remix Icon
import { RiOrganizationChart } from "react-icons/ri";

// VS Code Icon
import { VscOrganization } from "react-icons/vsc";

// Bootstrap Icon
import { BsUiChecks } from "react-icons/bs";
import ErrorModal from "./ui/ErrorModal";

const StepTabs = ({ hasUnsavedChanges, searchParams }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [modalVisible, setModalVisible] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [isEdd, setIsEdd] = useState(diligenceObservable.isEnhanced());
  const eddData = JSON.parse(localStorage.getItem("EDDFormData")) || {};
  const isEddValidated = eddData.status === "Validated";
  const isBypassUser = ["mlno", "cod"].includes(userSession.getUserType()?.toLowerCase());
  const [isRiskAssesCont, setIsRiskAssesCont] = useState(riskAssesContObservable.isContinue());
  const [isSticky, setIsSticky] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const stickyRef = useRef(null);
  const placeholderRef = useRef(null);

  // Engage sticky as soon as the page is scrolled. Release when scrolled
  // back to the top. Layout-independent so it can't get stuck.
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      setIsSticky(scrollY > 5);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // const [requestId, setRequestId] = useState( || '');
  const requestId = useMemo(() => sharedData.get("requestId"), []);

  // const entityId = useMemo(() => sharedData.get("entityId"), []);
  const transactionId = useMemo(() => sharedData.get("transactionId"), []);
  const propertyId = useMemo(() => sharedData.get("propertyId"), []);
  const status = useMemo(() => sharedData.get("status"), []);
 const [entityId, setEntityId] = useState(sharedData.get("entityId"));
const isEntityIdMissing = !entityId;

// Check if Client/Counterparty is completed (Validated)
// If entityId already exists on mount (edit mode), assume it's completed
const [isClientCounterpartyCompleted, setIsClientCounterpartyCompleted] = useState(() => {
  // If entityId exists from the start, Client/Counterparty must have been validated
  const initialEntityId = sharedData.get("entityId");
  if (initialEntityId) {
    return true;
  }
  // Otherwise check localStorage
  const clientFormData = localStorage.getItem("clientFormData");
  if (clientFormData) {
    const parsedData = JSON.parse(clientFormData);
    return parsedData.entityStatus === "Validated" || parsedData.status === "Validated";
  }
  return false;
});
// Check if "No Transaction" was selected
const [isNoTransaction, setIsNoTransaction] = useState(() => {
  const clientFormData = localStorage.getItem("clientFormData");
  if (clientFormData) {
    const parsedData = JSON.parse(clientFormData);
    return parsedData.clientType === "No Transaction";
  }
  return false;
});

useEffect(() => {
  const unsubscribe = sharedData.subscribe("entityId", (newValue) => {
    setEntityId(newValue);
    // If entityId becomes available, mark Client/Counterparty as completed
    if (newValue) {
      setIsClientCounterpartyCompleted(true);
    }
  });
  return () => unsubscribe();
}, []);

useEffect(() => {
  // Check Client/Counterparty validation status from localStorage
  const clientFormData = localStorage.getItem("clientFormData");
  if (clientFormData) {
    const parsedData = JSON.parse(clientFormData);
    const isValidated = parsedData.entityStatus === "Validated" || parsedData.status === "Validated";
    setIsClientCounterpartyCompleted(isValidated);
    // Check if "No Transaction" was selected
    const hasNoTransaction = parsedData.clientType === "No Transaction";
    setIsNoTransaction(hasNoTransaction);
    console.log("🔍 Client/Counterparty Validation Status:", isValidated, "No Transaction:", hasNoTransaction);
  } else if (entityId) {
    // If entityId exists but no localStorage data (edit mode), assume validated
    setIsClientCounterpartyCompleted(true);
  }
}, [entityId]);

const iconMap = {
  "Transaction Details": <Handshake size={20} />,
  "Property Info": <MapPinCheck size={20} />,
  "Client/Counterparty Due Diligence": <UserRoundPen size={20} />, // or use <FileUser size={20} />
  "Corporate Structure": <RiOrganizationChart size={20} />,
  "Management Structure": <VscOrganization size={20} />,
  "Risk Assessment": <ShieldQuestion size={20} />,
  "Risk Assessment Continue": <ShieldEllipsis size={20} />,
  "Enhanced Due Diligence": <ShieldPlus size={20} />,
  "Final Review": <BsUiChecks size={20} />,
};

  // useEffect(() => {
  //     sharedData.subscribe("requestId", (value) => {
  //       setRequestId(value);
  //       console.log("✅ Request ID updated:", value);
  //     });
  //   },[]);

  useEffect(()=>{
    const subscription = diligenceObservable.subscribe(() => {
      setIsEdd(diligenceObservable.isEnhanced());
    });
     // ✅ Force re-check on load from sharedData
  setIsRiskAssesCont(riskAssesContObservable.isContinue());
    return () => subscription.unsubscribe();
  }, []);

 useEffect(() => {
  const subscription = riskAssesContObservable.subscribe(() => {
    setIsRiskAssesCont(riskAssesContObservable.isContinue());
  });

  // ✅ Force re-check on load from sharedData
  setIsRiskAssesCont(riskAssesContObservable.isContinue());

  return () => subscription.unsubscribe();
}, []);

// Corporate/Management are never locked any more: under Simplified they are optional, not
// forbidden, so the tab stays clickable and the page itself says it is not required. If
// the user chooses to fill it, the backend flips the section to Not Validated and it must
// then be completed and validated like any other section.
// The statuses are still fetched, because whether a section is skipped is the server's
// call ("Not Applicable" = Simplified AND no data saved) and navigationHelper needs it to
// decide what Save+Next skips. sharedData alone was wrong for this: it is empty in the
// MLNO's browser, and it knows nothing about whether data was saved.
useEffect(() => {
  const requestId = sharedData.get("requestId");
  if (!requestId) return;

  const fetchSectionStatuses = async () => {
    try {
      const response = await fetch(`${config["backend-url"]}/get-all-statuses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userSession.getToken()}`,
        },
        body: JSON.stringify({ request_id: requestId }),
      });
      if (!response.ok) return;
      const result = await response.json();
      const statuses = result.statuses || {};
      // navigationHelper (Save+Next / Back), the page banners and the validation hooks all
      // read this one value, so they cannot disagree about whether a section is skipped.
      sharedData.set("sectionStatuses", {
        corporate: statuses.corporate_structure_status,
        management: statuses.management_data_status,
      });
    } catch (err) {
      console.error("❌ Error fetching section statuses in StepTabs", err);
    }
  };

  fetchSectionStatuses();
}, [location.pathname]);

const [riskAssesContinueFromBackend, setRiskAssesContinueFromBackend] = useState(false);
useEffect(() => {
  const requestId = sharedData.get("requestId");

  if (requestId) {
    const fetchRiskContinueStatus = async () => {
      try {
        const response = await fetch(`${config["backend-url"]}/check_risk_Continued_status`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userSession.getToken()}`,
          },
          body: JSON.stringify({ request_id: requestId }),
        });

        const result = await response.json();
        console.log("result from backend on nav", result);

        if (result.riskcontinuedflag === true) {
          setRiskAssesContinueFromBackend(true);
        }
      } catch (err) {
        console.error("❌ Error checking risk continue status in StepTabs", err);
      }
    };

    fetchRiskContinueStatus();
  }
}, []);
useEffect(() => {
  console.log("✅ Updated riskAssesContinueFromBackend:", riskAssesContinueFromBackend);
}, [riskAssesContinueFromBackend]);

  let baseSteps = [
    {
      label: "Transaction Details",
      path: appendSearchParams("/transaction-detail"),
      disabled: isNoTransaction && isClientCounterpartyCompleted,
      disabledMessage: "Transaction is disabled when 'No Transaction' is selected"
    },
    { label: "Property Info", path: appendSearchParams("/property-info") },
    { label: "Client/Counterparty Due Diligence", path: appendSearchParams("/client-counter") },
    // Not locked under Simplified — optional, not forbidden. The page carries the
    // "not required" notice, and filling it makes the section required.
    {
      label: "Corporate Structure",
      path: appendSearchParams("/corporate-structure"),
      disabled: isEntityIdMissing || !isClientCounterpartyCompleted,
      disabledMessage: "Please complete and validate Client/Counterparty first"
    },
    {
      label: "Management Structure",
      path: appendSearchParams("/management-structure"),
      disabled: isEntityIdMissing || !isClientCounterpartyCompleted,
      disabledMessage: "Please complete and validate Client/Counterparty first"
    },
    {
      label: "Risk Assessment",
      path: appendSearchParams("/RiskAsses"),
      // Gated like Corporate/Management, which it was not — the message already promised
      // this check while the condition only tested entityId. Risk Assessment can escalate
      // diligence to Enhanced, and that writes back to the entity; doing so from an
      // unvalidated Client/Counterparty is the same cross-section write that this branch
      // exists to stop.
      disabled: isEntityIdMissing || !isClientCounterpartyCompleted,
      disabledMessage: "Please complete and validate Client/Counterparty first"
    },
    {
      label: "Final Review",
      path: appendSearchParams("/FinalReview"),
      disabled: isEntityIdMissing || (!isBypassUser && isEdd && !isEddValidated),
      disabledMessage: isEntityIdMissing
        ? "Please complete Client/Counterparty first"
        : "Please validate Enhanced Due Diligence before proceeding to Final Review"
    },
  ];
if (isRiskAssesCont || riskAssesContinueFromBackend) {

    let RiskAssesContPath = "/RiskAssesContinue";
    const riskAssesStep = {
      label: "Risk Assessment Continue",
      path: appendSearchParams(RiskAssesContPath),
      disabled: isEntityIdMissing || !isClientCounterpartyCompleted,
      disabledMessage: "Please complete and validate Client/Counterparty first"
    }

    const insertIndex = baseSteps.findIndex(step => step.label === "Risk Assessment");
    if (insertIndex !== -1) {
      baseSteps.splice(insertIndex + 1, 0, riskAssesStep);
    }
  }

  if (isEdd) {
    let EddPath = "/Edd";
    const eddStep = {
      label: "Enhanced Due Diligence",
      path: appendSearchParams(EddPath),
      disabled: isEntityIdMissing || !isClientCounterpartyCompleted,
      disabledMessage: "Please complete and validate Client/Counterparty first"
    };
  
    const finalReviewIndex = baseSteps.findIndex(step => step.label === "Final Review");
    if (finalReviewIndex !== -1) {
      baseSteps.splice(finalReviewIndex, 0, eddStep); // Insert before Final Review
    }
  }

  // if (isEdd) {
  //   let EddPath = "/Edd";
  //   const eddStep = {
  //     label: "Enhanced Due Diligence",
  //     path: appendSearchParams(EddPath),
  //     disabled: isEntityIdMissing,
  //   }

  //   const insertIndex = baseSteps.findIndex(step => step.label === "Risk Assessment");
  //   if (insertIndex !== -1) {
  //     baseSteps.splice(insertIndex + 1, 0, eddStep);
  //   }
  // }

  const steps = baseSteps;

  function appendSearchParams(path) {
    return `${path}?${searchParams?.toString() || ""}`;
  }

  const handleNavigation = (path, disabled, disabledMessage) => {
    if (disabled) {
      if (disabledMessage) {
        setErrorMessage(disabledMessage);
        setErrorModalOpen(true);
      }
      return;
    }
    if (hasUnsavedChanges) {
      setPendingPath(path);
      setModalVisible(true);
    } else {
      navigate(path, { state: { requestId, transactionId, status, entityId, propertyId } });
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setPendingPath(null);
  };

  return (
    <>
      {/* Placeholder to prevent content jump when element becomes fixed */}
      <div ref={placeholderRef} className={`step-tabs-placeholder ${isSticky ? 'show' : ''}`} style={{ height: isSticky && stickyRef.current ? stickyRef.current.offsetHeight : 0 }}></div>
      <div ref={stickyRef} className={`step-tabs-wrapper ${isSticky ? 'is-sticky' : ''}`}>
        {!isSticky && <h2>Request Id: {sharedData.get("requestId")}</h2>}
        <div className="step-tabs">
          {steps.map((step, index) => {
            const isActive = location.pathname === new URL(step.path, window.location.origin).pathname;
            return (
              <div className="step-container" key={index}>
                <div
                  className={`step ${isActive ? "active" : ""} ${step.disabled ? "disabled" : ""}`}
                  onClick={() => handleNavigation(step.path, step.disabled, step.disabledMessage)}
                >
                  <div className="step-icon-wrapper">
                    <div className="icon-circle">{iconMap[step.label]}</div>
                  </div>
                  <div className="step-label">{step.label}</div>
                  {step.disabled && step.disabledMessage && (
                    <div className="step-tooltip">{step.disabledMessage}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <ErrorModal isOpen={errorModalOpen} message={errorMessage} onClose={() => setErrorModalOpen(false)} />
    </>
  );
};

export default StepTabs;
